import React, { useEffect, useState, useRef } from 'react'

import PropTypes from 'prop-types'
import styled from 'styled-components'

import { useOnMount, usePrevious } from 'core/hooks'
import variables from 'core/styles/variables'

import CodeSentStatus from './CodeSentStatus'

const CodeInput = styled.input`
  width: 200px;
  padding: 4px;
  margin-top: 40px;
  font-family: 'Roboto Mono', monospace;
  font-size: 28px;
  line-height: 1;
  color: ${variables.colorBlack60};
  text-align: center;
  background-color: transparent;
  border: none;

  &:disabled {
    color: ${variables.colorBlack40};
    background-color: ${variables.colorBlack10};
  }

  /* Hide Arrows From Input Number */

  /* Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    margin: 0;
    appearance: none;
  }

  /* Firefox */
  &[type='number'] {
    appearance: textfield;
  }
`
const Subtitle = styled.div`
  margin-top: 64px;
  font-size: 14px;
  color: ${variables.colorBlack90};
  text-align: center;
`
const Text = styled.div`
  margin-top: ${(p) => p.marginTop};
  margin-bottom: ${(p) => p.marginBottom};
  font-size: 12px;
  font-weight: 400;
  color: ${variables.colorBlack50};
`

const Actions = styled.div`
  margin-top: 24px;
  margin-bottom: 24px;
`

const Action = styled.button`
  padding: 4px 8px;
  margin-left: 16px;
  font-size: 14px;
  font-weight: 500;
  color: ${variables.colorBluePrimary};
  cursor: pointer;
  background-color: transparent;
  border: none;

  &:first-child {
    margin-left: 0;
  }

  &:hover {
    text-decoration: underline;
  }
`

const OneTimeCodeForm = (props) => {
  const {
    onOneTimeCode,
    sending,
    subtitle,
    onTextMe,
    onEmailMe,
    onCallMe,
    sentMessage,
    onTryAnotherWay,
    tryAnotherWayLabel,
  } = props

  const [isMobile] = useState(() => 'ontouchstart' in window)

  const [code, setCode] = useState('')

  const previousCode = usePrevious(code)

  useEffect(() => {
    if (previousCode !== code && code.length === 6 && !sending) {
      onOneTimeCode(code)
    }
  }, [code, previousCode, sending, onOneTimeCode])

  const inputRef = useRef()

  useOnMount(() => {
    inputRef.current.focus()
  })

  const handleChange = (val = '') => {
    // strip non-alphanumeric characters and trim to 6 places
    setCode(val.replace(/[^0-9]/gi, '').slice(0, 6))
  }

  const handleTextMe = () => {
    setCode('')
    if (onTextMe) onTextMe()
  }

  const handleCallme = () => {
    setCode('')
    if (onCallMe) onCallMe()
  }

  const handleEmailMe = () => {
    setCode('')
    if (onEmailMe) onEmailMe()
  }

  return (
    <>
      <Subtitle>{subtitle}</Subtitle>
      <CodeInput
        ref={inputRef}
        placeholder='000000'
        value={code}
        onChange={(event) => handleChange(event.target.value)}
        disabled={sending}
        type={isMobile ? 'number' : 'text'}
        inputmode='numeric'
        pattern='[0-9]*'
      />
      <Text marginTop='80px'>Didn't get the code?</Text>
      <Actions>
        {onTextMe && <Action onClick={handleTextMe}>Text me</Action>}
        {onCallMe && <Action onClick={handleCallme}>Call me</Action>}
        {onEmailMe && <Action onClick={handleEmailMe}>Email me</Action>}
      </Actions>

      <CodeSentStatus sending={sending} label={sentMessage} />

      {!!(onTryAnotherWay && tryAnotherWayLabel) && (
        <Actions>
          <Action onClick={onTryAnotherWay}>{tryAnotherWayLabel}</Action>
        </Actions>
      )}
    </>
  )
}

OneTimeCodeForm.propTypes = {
  onOneTimeCode: PropTypes.func,
  subtitle: PropTypes.string,
  sending: PropTypes.bool,
  onTextMe: PropTypes.func,
  onEmailMe: PropTypes.func,
  onCallMe: PropTypes.func,
  onTryAnotherWay: PropTypes.func,
  tryAnotherWayLabel: PropTypes.node,
  sentMessage: PropTypes.node,
}

export default OneTimeCodeForm
